import React, { useRef, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Divider,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { GlobalStyles } from "@mui/material";
import ReactToPrint, { useReactToPrint } from "react-to-print";

const AuditReportDownload = ({ data }) => {
  const reportRef = useRef();
  const componentRef = useRef();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  if (!data) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 6, px: 10 }}>
      {data ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "10px",
            margin: "10px",
          }}
        >
          <ReactToPrint
            trigger={() => (
              <Button variant="contained" size="small" onClick={handlePrint}>
                Print
              </Button>
            )}
            content={() => componentRef.current}
          />
        </Box>
      ) : null}
      <GlobalStyles
        styles={{
          "@media print": {
            body: {
              margin: 0,
            },
            ".page-section": {
              pageBreakInside: "avoid",
              breakInside: "avoid",
              marginTop: "40px",
              marginBottom: "40px",
            },
          },
          ".page-section": {
            marginTop: "40px",
            marginBottom: "40px",
          },
        }}
      />

      <Box ref={componentRef}>
        <Box
          sx={{
            py: 4,
            px: 10,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            breakInside: "avoid",
            overflow: "hidden",
            zoom: 0.9,
          }}
        >
          <Box className="page-section">
            <Box sx={{ textAlign: "center", mb: 5 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{ fontWeight: 700, color: theme.palette.text.primary }}
              >
                Stock and Pallet Audit Report
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Generated on:{" "}
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              <Divider sx={{ maxWidth: 200, mx: "auto", mb: 3 }} />
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: theme.palette.text.primary,
                }}
              >
                Stock Summary
              </Typography>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 1,
                  boxShadow: theme.shadows[2],
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          py: 2,
                          px: 3,
                        }}
                      >
                        Metric
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          py: 2,
                          px: 3,
                        }}
                      >
                        Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      {
                        label: "Match Status",
                        value: data.stockSummary.matched,
                      },
                      {
                        label: "Total Items Scanned",
                        value: data.stockSummary.totalItemsScanned,
                      },
                      {
                        label: "Total Items Found",
                        value: data.stockSummary.totalItemsFound,
                      },
                      //   {
                      //     label: "Total Duplicate Items Scanned",
                      //     value: data.stockSummary.totalDuplicateItemsScanned,
                      //   },
                      //   {
                      //     label: "Total Extra Items",
                      //     value: data.stockSummary.totalExtraItems,
                      //   },
                      //   {
                      //     label: "Total Missing Items",
                      //     value: data.stockSummary.totalMissingItems,
                      //   },
                    ].map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1.5, px: 3 }}>
                          {row.label}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, px: 3 }}>
                          {row.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {Object.keys(data.palletSummary).map((palletKey, index) => (
              <Box key={palletKey} sx={{ mb: 6 }} className="page-break">
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: theme.palette.text.primary,
                  }}
                >
                  Pallet Summary ({palletKey})
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 1,
                    boxShadow: theme.shadows[2],
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            py: 2,
                            px: 3,
                          }}
                        >
                          Metric
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            py: 2,
                            px: 3,
                          }}
                        >
                          Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.palletSummary[palletKey].matched === "YES" ? (
                        <TableRow>
                          <TableCell sx={{ py: 1.5, px: 3 }}>
                            Match Status
                          </TableCell>
                          <TableCell sx={{ py: 1.5, px: 3 }}>
                            {data.palletSummary[palletKey].matched}
                          </TableCell>
                        </TableRow>
                      ) : (
                        [
                          {
                            label: "Match Status",
                            value: data.palletSummary[palletKey].matched,
                          },
                          {
                            label: "Total Items Scanned",
                            value:
                              data.palletSummary[palletKey].totalItemsScanned,
                          },
                          {
                            label: "Total Items Found",
                            value:
                              data.palletSummary[palletKey].totalItemsFound,
                          },
                        ].map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <TableCell sx={{ py: 1.5, px: 3 }}>
                              {row.label}
                            </TableCell>
                            <TableCell sx={{ py: 1.5, px: 3 }}>
                              {row.value}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {data.palletSummary[palletKey].totalExtraItems.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Extra Items Detected ({palletKey})
                    </Typography>
                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 1,
                        boxShadow: theme.shadows[2],
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              MSN
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              PALLET CODE
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              Expected PALLET CODE
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.palletSummary[palletKey].totalExtraItems.map(
                            (item, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <TableCell sx={{ py: 1.5, px: 3 }}>
                                  {item.msn}
                                </TableCell>
                                <TableCell sx={{ py: 1.5, px: 3 }}>
                                  {item.palletCode}
                                </TableCell>
                                <TableCell sx={{ py: 1.5, px: 3 }}>
                                  {item.expectedPalletCode}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {data.palletSummary[palletKey].totalMissingItems.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Missing Items ({palletKey})
                    </Typography>
                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 1,
                        boxShadow: theme.shadows[2],
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              MSN
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              ITEM DESCRIPTION
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.palletSummary[palletKey].totalMissingItems.map(
                            (item, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <TableCell sx={{ py: 1.5, px: 3 }}>
                                  {item.msn}
                                </TableCell>
                                <TableCell sx={{ py: 1.5, px: 3 }}>
                                  {item.itemDescription}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {data.palletSummary[palletKey].totalDuplicatedItemsScanned
                  .length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Duplicated Items Scanned ({palletKey})
                    </Typography>
                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 1,
                        boxShadow: theme.shadows[2],
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              MSN
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                py: 2,
                                px: 3,
                              }}
                            >
                              PALLET CODE
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.palletSummary[
                            palletKey
                          ].totalDuplicatedItemsScanned.map((item, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                "&:nth-of-type(odd)": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <TableCell sx={{ py: 1.5, px: 3 }}>
                                {item.msn}
                              </TableCell>
                              <TableCell sx={{ py: 1.5, px: 3 }}>
                                {item.palletCode}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            ))}

            <Box
              sx={{
                mt: 5,
                textAlign: "center",
                color: theme.palette.text.secondary,
              }}
            >
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                © {new Date().getFullYear()} Inventory Management System. All
                rights reserved.
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                This report is generated for internal audit purposes only.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AuditReportDownload;
